import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import * as moment from 'moment';

import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EventManager, AlertService, JhiLanguageService } from 'ng-jhipster';

import { Reservation } from './reservation.model';
import { Card, CardService } from '../card';
import { ReservationPopupService } from './reservation-popup.service';
import { ReservationService } from './reservation.service';
import { School, SchoolService } from '../school';
import { Student, StudentService, StudentTypeaheadService } from '../student';
import { Lesson, LessonService } from '../lesson';
import { ReservationCancellationReason, ReservationCancellationReasonService } from '../reservation-cancellation-reason';
import { studentGlobalLocations } from '../../shared/constants/student.locations.constants';
import { AbstractDialogComponent } from '../abstract-dialog-component/abstract-dialog.component';
import { Principal, CRUD_ACTIONS } from '../../shared/index';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
    selector: 'jhi-reservation-dialog',
    templateUrl: './reservation-dialog.component.html'
})
export class ReservationDialogComponent extends AbstractDialogComponent implements OnInit {

    public static readonly MAX_DAYS_TO_SHOW = 7;
    reservation: Reservation;
    authorities: any[];
    isSaving: boolean;
    isUpdating: boolean = false;
    isCreatingForLesson: boolean = false;
    bypassRules: boolean;
    overwriteCard: boolean;
    lessons: Lesson[];
    reservationcancellationreasons: ReservationCancellationReason[];
    search = this.studentTypeaheadService.search;
    resultFormatter = this.studentTypeaheadService.resultFormatter;
    inputFormatter = (student: any) => this.studentTypeaheadService.inputFormatter(student, () => this.reservation.student['user']);
    compatibleCards: Card[];

    constructor(
        public activeModal: NgbActiveModal,
        private jhiLanguageService: JhiLanguageService,
        alertService: AlertService,
        private reservationService: ReservationService,
        private cardService: CardService,
        schoolService: SchoolService,
        private studentService: StudentService,
        private lessonService: LessonService,
        private reservationCancellationReasonService: ReservationCancellationReasonService,
        private eventManager: EventManager,
        private studentTypeaheadService: StudentTypeaheadService,
        principal: Principal
    ) {
        super(schoolService, alertService, principal);
    }

    ngOnInit() {
        this.isSaving = false;
        this.overwriteCard = false;
        this.authorities = ['ROLE_USER', 'ROLE_ADMIN'];
        this.isUpdating = this.reservation.id ? true : false;

        this.loadSchools();

        if (this.reservation.lesson) {
            this.isCreatingForLesson = true;
            this.lessons = [];
            this.lessons.push(this.reservation.lesson);
        } else {
            this.lessonService.search({
                query: this.getLessonsQuery(),
                sort: this.lessonsSort()
            })
            .subscribe(
            (res: HttpResponse<Lesson[]>) => {
                let lessons: Lesson[] = res.body;
                let reservationLesson: Lesson = this.reservation ? this.reservation.lesson : null;
                this.lessons = lessons;
            },
            (res: HttpErrorResponse) => this.onError(res));
        }

        this.loadCompatibleCards();
    }

    loadCompatibleCards(): void {
        if (!(this.reservation.student instanceof Object)
            || !this.reservation.student.id
            || !this.reservation.lesson) {
            // we cannot load compatible cards without a student nor a lesson
            return;
        }

        const lessonDate = moment(this.reservation.lesson.begining).format('YYYY-MM-DD');

        // Lucene syntax: validSince <= lessonDate <= validUntil
        const query = `student.id:${this.reservation.student.id} AND validSince:[* TO ${lessonDate}] AND validUntil:[${lessonDate} TO *]`;

        this.cardService.search({ query }).subscribe(
            (res: HttpResponse<Card[]>) => {
                let availableCardsForDate = res.body || [];

                if (!availableCardsForDate || availableCardsForDate.length === 0) {
                    this.compatibleCards = [];
                }

                this.compatibleCards = availableCardsForDate.filter(card => card.maxAssistancesNum > card.usedReservations);
                if (this.reservation.card) {
                    // do not change the card if the reservation alrady has a card
                    const cardIsInTheList = this.compatibleCards.find(card => card.id === this.reservation.card.id);
                    if (!cardIsInTheList) {
                        this.compatibleCards.push(this.reservation.card);
                    }
                } else {
                    // automatically select a card with available places
                    const cardWithAvailablePlaces = this.compatibleCards.find(card => card.maxAssistancesNum > card.usedReservations);
                    if (cardWithAvailablePlaces) {
                        this.reservation.card = cardWithAvailablePlaces;
                    }
                }
            },
            (error) => {
                this.onError(error);
            }
        );
    }

    onCardSelected(selectedCard: Card | null): void {
        this.overwriteCard = true;
    }

    onStudentChanged(): void {
        this.loadCompatibleCards();
    }

    onLessonChanged(): void {
        this.loadCompatibleCards();
    }

    showCompatibleCardsList(): boolean {
        return this.compatibleCards
        && this.compatibleCards.length >= 1;
    }

    showNoCardMessage(): boolean {
        return (this.reservation.student instanceof Object)
            && this.reservation.student.id
            && this.reservation.lesson
            && !this.reservation.card
            && (!this.compatibleCards || this.compatibleCards.length === 0);
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    save(form: NgForm) {
        if (this.reservation.student instanceof Object) {
            this.isSaving = true;
            if (this.reservation.id !== undefined) {
                this.reservationService.update(this.reservation, this.bypassRules, this.overwriteCard)
                    .subscribe((res: Reservation) =>
                        this.onSaveSuccess(res, CRUD_ACTIONS.UPDATE), (res: HttpErrorResponse) => this.onSaveError(res));
            } else {
                this.reservationService.create(this.reservation, this.bypassRules, this.overwriteCard)
                    .subscribe((res: Reservation) =>
                        this.onSaveSuccess(res, CRUD_ACTIONS.CREATE), (res: HttpErrorResponse) => this.onSaveError(res));
            }
        } else {
            let studentInput = form !== undefined ? form.controls['student'] : null;
            if (studentInput !== null) {
                studentInput.reset();
            }
        }
    }

    private getLessonsQuery(): string {
        const fromDate = new Date();
        const toDate = new Date();
        toDate.setDate(fromDate.getDate() + ReservationDialogComponent.MAX_DAYS_TO_SHOW);

        const datePipe = new DatePipe('en'); // 'en' is valid, we add 'UTC' in transform
        const from = datePipe.transform(fromDate, 'yyyy-MM-dd', 'UTC');
        const to = datePipe.transform(toDate, 'yyyy-MM-dd', 'UTC');

        return `begining:[${from} TO ${to}]`;
    }

    private onSaveSuccess(result: Reservation,  action: String) {
        this.eventManager.broadcast({ name: 'reservationListModification', content: {
            action: action,
            content: result
        } });
        this.isSaving = false;
        this.activeModal.dismiss(result);
    }

    private onSaveError(error) {
        this.isSaving = false;
        this.onError(error);
    }

    trackSchoolById(index: number, item: School) {
        return item.id;
    }

    trackStudentById(index: number, item: Student) {
        return item.id;
    }

    trackLessonById(index: number, item: Lesson) {
        return item.id;
    }

    trackReservationCancellationReasonById(index: number, item: ReservationCancellationReason) {
        return item.id;
    }

    lessonsSort() {
        return ['begining', 'desc'];
    }
}

@Component({
    selector: 'jhi-reservation-popup',
    template: ''
})
export class ReservationPopupComponent implements OnInit, OnDestroy {

    modalRef: NgbModalRef;
    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private reservationPopupService: ReservationPopupService
    ) { }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(params => {
            if (params['id']) {
                this.modalRef = this.reservationPopupService
                    .open(ReservationDialogComponent as Component, params['id']);
            } else if (params['studentId']) {
                this.modalRef = this.reservationPopupService
                    .open(ReservationDialogComponent as Component, undefined, params['studentId']);
            } else if (params['lessonId']) {
                this.modalRef = this.reservationPopupService
                    .open(ReservationDialogComponent as Component, undefined, undefined, params['lessonId']);
            }

        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/entities/reservation/reservation-dialog.component.ts